import React, { ReactElement, useState } from "react";
import {
  ADDITIONAL_PROPERTY_FLAG,
  FieldTemplateProps,
  TranslatableString
} from "@rjsf/utils";
import Input from "@amzn/awsui-components-react-v3/polaris/input";
import FormField from "../Fields/FormField";
import {
  description,
  label,
  placeholder,
  shouldUseContainer,
  title
} from "../utils";
import Container from "@amzn/awsui-components-react-v3/polaris/container";
import Header from "@amzn/awsui-components-react-v3/polaris/header";
import Grid from "@amzn/awsui-components-react-v3/polaris/grid";
import Button from "@amzn/awsui-components-react-v3/polaris/button";

/** The `WrapIfAdditional` component is used by the `FieldTemplate` to rename, or remove properties that are
 * part of an `additionalProperties` part of a schema.
 *
 * @param props - The `WrapIfAdditionalProps` for this component
 */
export default function WrapIfAdditionalTemplate(
  props: FieldTemplateProps
): ReactElement {
  const additional = ADDITIONAL_PROPERTY_FLAG in props.schema;
  if (!additional) {
    const _title = title(props);
    // Special handling for root: Do not use a container if object with no title
    if (props.id === "root" && props.schema.type === "object" && !_title) {
      return <>{props.children}</>;
    }

    // Defer objects and arrays to their respective templates, but wrap with a
    // container here (not in their templates) due to the special root handling
    if (
      props.schema.type === "object" ||
      (props.schema.type === "array" && !props.schema.uniqueItems)
    ) {
      return (
        <Container
          header={
            _title && <Header description={description(props)}>{_title}</Header>
          }
        >
          {props.children}
        </Container>
      );
    }

    // Primitive field means FormField (and allow container wrapper)
    return (
      <ConditionalContainer {...props}>
        <FormField {...props} />
      </ConditionalContainer>
    );
  }

  const [localValue, setLocalValue] = useState<string | undefined>(
    label(props)
  );

  return (
    <div style={{ borderBottom: "1px solid #d5dbdb", paddingBottom: "20px" }}>
      <Grid
        gridDefinition={[
          { colspan: { default: 12, xxs: 3 } },
          { colspan: { default: 12, xxs: 6 } },
          { colspan: { default: 12, xxs: 3 } }
        ]}
      >
        <FormField {...(props as FieldTemplateProps)} stretch>
          <Input
            data-id={props.id}
            type="text"
            placeholder={placeholder(props) || label(props)}
            value={localValue || ""}
            onBlur={() => props.onKeyChange(localValue || props.label)}
            onChange={event => setLocalValue(event.detail.value)}
            disabled={props.disabled}
            readOnly={props.readonly}
            ariaRequired={props.required}
          />
        </FormField>
        <FormField
          {...(props as FieldTemplateProps)}
          label={props.label}
          stretch
        >
          {props.children}
        </FormField>
        <Button
          formAction="none"
          disabled={props.disabled || props.readonly}
          onClick={props.onDropPropertyClick(props.label)}
        >
          {props.registry.translateString(TranslatableString.RemoveButton)}
        </Button>
      </Grid>
    </div>
  );
}
/**
 * Helper component for field rendering. Conditionally renders a container if
 * `awsui-util-container` class is present or if unique items special case.
 */
const ConditionalContainer: React.FC<FieldTemplateProps> = props => {
  const isUniqueItemsArray =
    props.schema.type === "array" && props.schema.uniqueItems;
  return isUniqueItemsArray || shouldUseContainer(props) ? (
    <Container
      header={
        title(props) && (
          <Header description={isUniqueItemsArray ? null : description(props)}>
            {title(props)}
          </Header>
        )
      }
    >
      {props.children}
    </Container>
  ) : (
    props.children
  );
};
